import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Stack,
  Paper,
  IconButton,
  Alert,
  CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import WarningIcon from '@mui/icons-material/Warning';
import { useSelector } from 'react-redux';
import tokenTransferServices from '../../services/tokenTransferServices';
import CompletePaymentModal from './CompletePaymentModal';

const AcceptOfferModal = ({ open, data, onClose, onSuccess }) => {
  const user = useSelector(state => state.user.data);
  const [loading, setLoading] = useState(false);
  const [showCompletePaymentModal, setShowCompletePaymentModal] = useState(false);
  
  if (!open || !data) return null;
  
  const handleAcceptOffer = () => {
    // Show the complete payment modal without closing the accept offer modal
    setShowCompletePaymentModal(true);
  };

  const handleCloseCompletePaymentModal = () => {
    setShowCompletePaymentModal(false);
  };

  const handlePaymentSuccess = () => {
    setShowCompletePaymentModal(false);
    onClose(); // Close the accept offer modal after successful payment
    onSuccess && onSuccess();
  };

  

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
        }
      }}
    >
      <DialogTitle sx={{ 
        fontWeight: 600, 
        fontSize: "1.5rem",
        borderBottom: '1px solid',
        borderColor: 'divider',
        pb: 2,
        position: 'relative'
      }}>
        Accept Offer
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: 'grey.500'
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ pt: 3 }}>
        <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
          Review the offer details and sender's bank information before accepting.
        </Typography>

        {/* Warning Alert */}
        <Alert 
          severity="info" 
          icon={<WarningIcon />}
          sx={{ 
            mb: 3,
            '& .MuiAlert-message': {
              fontSize: '0.875rem'
            }
          }}
        >
          By accepting this offer, you agree to transfer the payment to the sender's bank account.
        </Alert>

        <Stack spacing={3}>
          {/* Offer Details */}
          <Paper elevation={0} sx={{ p: 3, bgcolor: 'grey.50', borderRadius: 2 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Offer Details
            </Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Box
                sx={{
                  width: 60,
                  height: 60,
                  borderRadius: 1,
                  bgcolor: 'grey.300',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: 2
                }}
              >
                <Typography variant="h6" color="text.secondary">
                  📄
                </Typography>
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {data.assetName || 'Tech Park Commercial'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {data.tokens || '15 tokens'}
                </Typography>
              </Box>
            </Box>

           
          </Paper>

          {/* Sender's Bank Details */}
          <Paper elevation={0} sx={{ p: 3, bgcolor: 'grey.50', borderRadius: 2 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Sender's Bank Details
            </Typography>
            
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">Account Name:</Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {data?.senderBankAccount.accHolderName || data?.legalName}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">Account Number:</Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {data?.senderBankAccount.accountNumber || '1234567890'}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">IFSC Code:</Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {data?.senderBankAccount.ifscCode || 'HDFC0001234'}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">Bank:</Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {data?.senderBankAccount.bankName || 'HDFC Bank'}
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button 
          onClick={handleAcceptOffer}
          variant="contained"
          color="primary"
          sx={{ 
            minWidth: 150,
            bgcolor: 'error.main',
            '&:hover': {
              bgcolor: 'error.dark'
            }
          }}
        >
          Accept & Make Payment
        </Button>
        
        <Button 
          onClick={onClose}
          variant="outlined"
          sx={{ minWidth: 100 }}
        >
          Cancel
        </Button>
      </DialogActions>

      {/* Complete Payment Modal */}
      <CompletePaymentModal
        open={showCompletePaymentModal}
        data={data}
        onClose={handleCloseCompletePaymentModal}
        onSuccess={handlePaymentSuccess}
      />
    </Dialog>
  );
};

export default AcceptOfferModal;
